import { useEffect, useState } from "react";

import { useTranslation } from "react-i18next";

import { Link, useNavigate, useParams } from "react-router-dom";

import { Button } from "@/components/ui/button";

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

import { Input } from "@/components/ui/input";

import { Label } from "@/components/ui/label";

import { GridPattern } from "@/components/ui/grid-pattern";

import LoadingOverlay from "../../../shared/components/LoadingOverlay";

import { resetPassword, verifyResetToken } from "../api/authApi";

function Spinner() {
  return (
    <svg
      className="mr-2 h-4 w-4 animate-spin"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
      />
    </svg>
  );
}

const AuthBackground = () => (
  <GridPattern
    width={40}
    height={40}
    className="[mask-image:radial-gradient(600px_circle_at_center,white,transparent)] [stroke:var(--primary)] [stroke-opacity:0.2]"
  />
);

const ResetPasswordPage = () => {
  const { t } = useTranslation("common");

  const { token } = useParams<{ token: string }>();

  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(true);

  const [isValidToken, setIsValidToken] = useState(false);

  const [password, setPassword] = useState("");

  const [confirmPassword, setConfirmPassword] = useState("");

  const [error, setError] = useState<string | null>(null);

  const [isSuccess, setIsSuccess] = useState(false);

  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const checkToken = async () => {
      if (!token) {
        setIsValidToken(false);

        setIsLoading(false);

        return;
      }

      const valid = await verifyResetToken(token);

      setIsValidToken(valid);

      setIsLoading(false);
    };

    checkToken();
  }, [token]);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (password !== confirmPassword) {
      setError(t("RESET_PASSWORD.passwordMismatch"));

      return;
    }

    if (password.length < 8) {
      setError(t("RESET_PASSWORD.passwordTooShort"));

      return;
    }

    setError(null);

    setSubmitting(true);

    try {
      if (token) {
        await resetPassword(token, password);

        setIsSuccess(true);

        setTimeout(() => {
          navigate("/login");
        }, 3000);
      }
    } catch {
      setError(t("RESET_PASSWORD.errorResetting"));
    } finally {
      setSubmitting(false);
    }
  };

  if (isLoading) {
    return <LoadingOverlay />;
  }

  if (!isValidToken) {
    return (
      <div className="relative min-h-screen flex items-center justify-center px-4 overflow-hidden bg-background">
        <AuthBackground />
        <Card className="relative z-10 w-full max-w-md">
          <CardHeader className="text-center">
            <img
              src="/logo.png"
              alt="Eriol"
              className="h-12 w-12 mx-auto mb-6"
            />
            <CardTitle className="text-2xl">
              {t("RESET_PASSWORD.invalidTokenTitle")}
            </CardTitle>
            <CardDescription>
              {t("RESET_PASSWORD.invalidTokenMessage")}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-center text-sm text-muted-foreground">
              <Link to="/login" className="text-primary hover:underline">
                {t("FORGOT_PASSWORD.backToLogin")}
              </Link>
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen flex items-center justify-center px-4 overflow-hidden bg-background">
      <AuthBackground />
      <Card className="relative z-10 w-full max-w-md">
        <CardHeader className="text-center">
          <img src="/logo.png" alt="Eriol" className="h-12 w-12 mx-auto mb-6" />
          <CardTitle className="text-2xl">
            {t("RESET_PASSWORD.title")}
          </CardTitle>
          <CardDescription>{t("RESET_PASSWORD.subtitle")}</CardDescription>
        </CardHeader>
        <CardContent>
          {error && (
            <div className="rounded-lg border border-destructive/50 bg-destructive/10 text-destructive p-3 mb-4 text-center text-sm">
              {error}
            </div>
          )}

          {isSuccess ? (
            <div className="flex flex-col items-center gap-4 text-center">
              <div className="flex h-14 w-14 items-center justify-center rounded-full bg-green-500/10 text-green-400">
                <svg
                  className="h-7 w-7"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
              <p className="text-sm text-muted-foreground">
                {t("RESET_PASSWORD.successMessage")}
              </p>
              <p className="text-xs text-muted-foreground">
                {t("RESET_PASSWORD.redirecting")}
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="newPassword">
                  {t("RESET_PASSWORD.newPassword")}
                </Label>
                <Input
                  id="newPassword"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  autoComplete="new-password"
                  required
                  minLength={8}
                  disabled={submitting}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirmPassword">
                  {t("RESET_PASSWORD.confirmPassword")}
                </Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="••••••••"
                  autoComplete="new-password"
                  required
                  minLength={8}
                  disabled={submitting}
                />
              </div>
              <Button type="submit" className="w-full" disabled={submitting}>
                {submitting && <Spinner />}
                {t("RESET_PASSWORD.submit")}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ResetPasswordPage;
